import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ModelConfigurationProps } from './types';
import { MODEL_TYPES } from './constants';
import { useModelConfiguration } from './hooks';
import { NeuralNetworkParams } from './NeuralNetworkParams';
import { SVRParams } from './SVRParams';
import { LinearModelInfo } from './LinearModelInfo';
import { LGBMRParams } from './LGBMRParams';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

/**
 * ModelConfiguration component
 * Allows users to select model type and configure model-specific parameters
 */
export const ModelConfiguration: React.FC<ModelConfigurationProps> = memo(({
  value,
  onChange,
  disabled = false
}) => {
  const { t } = useTranslation('training');

  const {
    modelParams,
    validationErrors,
    handleParameterChange,
    handleModelTypeChange,
    isNeuralNetworkModel,
    isSVRModel,
    isLinearModel,
    isLGBMRModel,
    setValidationErrors
  } = useModelConfiguration(value, onChange);

  return (
    <div className="rounded-xl border border-border bg-card overflow-visible shadow-sm mb-4 sm:mb-6">
      <div className="px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <h2 className="text-base sm:text-lg font-semibold text-foreground tracking-tight text-center">
          {t('training.modelConfiguration.title')}
        </h2>
      </div>

      <div className="max-w-2xl mx-auto divide-y divide-border">
        {/* Model Type Selection */}
        <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-center gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
          <label
            htmlFor="model-config-mode"
            className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
          >
            {t('training.modelConfiguration.model')}
          </label>
          <div className="flex items-center gap-2">
            <select
              id="model-config-mode"
              value={modelParams.MODE}
              onChange={(e) => handleModelTypeChange(e.target.value)}
              disabled={disabled}
              className="w-full border border-input rounded-lg px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-foreground bg-card disabled:bg-muted disabled:text-muted-foreground transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
            >
              {MODEL_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {modelParams.MODE === 'Dense' && <InfoTooltip text={t('tooltips.models.dense.description')} width="w-96" />}
            {modelParams.MODE === 'CNN' && <InfoTooltip text={t('tooltips.models.cnn.description')} width="w-96" />}
            {modelParams.MODE === 'LSTM' && <InfoTooltip text={t('tooltips.models.lstm.description')} width="w-96" />}
            {modelParams.MODE === 'AR LSTM' && <InfoTooltip text={t('tooltips.models.arLstm.description')} width="w-96" />}
            {modelParams.MODE === 'SVR_dir' && <InfoTooltip text={t('tooltips.models.svrDir.description')} width="w-96" />}
            {modelParams.MODE === 'SVR_MIMO' && <InfoTooltip text={t('tooltips.models.svrMimo.description')} width="w-96" />}
            {modelParams.MODE === 'LGBMR' && <InfoTooltip text={t('tooltips.models.lgbmr.description')} width="w-96" />}
            {modelParams.MODE === 'LIN' && <InfoTooltip text={t('tooltips.models.lin.description')} width="w-96" />}
          </div>
        </div>

        {/* Neural Network Parameters */}
        {isNeuralNetworkModel() && (
          <NeuralNetworkParams
            modelParams={modelParams}
            disabled={disabled}
            validationErrors={validationErrors}
            onParameterChange={handleParameterChange}
            onValidationErrorChange={setValidationErrors}
          />
        )}

        {/* SVR Parameters */}
        {isSVRModel() && (
          <SVRParams
            modelParams={modelParams}
            disabled={disabled}
            onParameterChange={handleParameterChange}
          />
        )}

        {/* LGBMR Parameters */}
        {isLGBMRModel() && (
          <LGBMRParams
            modelParams={modelParams}
            disabled={disabled}
            onParameterChange={handleParameterChange}
          />
        )}

        {/* Linear Model Info */}
        {isLinearModel() && <LinearModelInfo />}
      </div>
    </div>
  );
});

ModelConfiguration.displayName = 'ModelConfiguration';

export default ModelConfiguration;
